import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { fadeInOutHeight } from '@app/animations/fade-in-out.animation';
import { AppConfig } from '@app/app.config';
import { ModalProvider } from '@app/components/_shared/modal/modal.component';
import { ActivationComponent } from '@app/components/account/activation/activation.component';
import { RegisterRequestModel } from '@app/models/auth/registerRequest.model';
import { RegisterResponseModel } from '@app/models/auth/registerResponse.model';
import { CountryModel } from '@app/models/user/country.model';
import { GenderModel } from '@app/models/user/gender.model';
import { AuthService } from '@app/services/auth.service';
import { LocalisationService } from '@app/services/localisation.service';
import { QuestionnaireService } from '@app/services/questionnaire.service';
import { UserService } from '@app/services/user.service';
import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';

import { RegisterCompleteComponent } from '../register-complete/register-complete.component';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  animations: [fadeInOutHeight]
})
export class RegisterComponent implements OnInit {
  static route: string = 'register';

  locale: any = {};

  showPassword: boolean = false;
  passwordType: string = 'password';
  passwordLength: number;

  registerForm: FormGroup;
  registerFormSubmitted: boolean = false;
  countries: CountryModel[] = null;
  genders: GenderModel[] = null;

  discountCodeError: string = null;
  emailError: string = null;

  discountCodeLabel: string = null;

  public formSubmitted: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private localisation: LocalisationService,
    private authService: AuthService,
    private userService: UserService,
    private modal: ModalProvider,
    private questionnaireService: QuestionnaireService,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    this.locale = this.localisation.getTranslationsForComponent('RegisterComponent');

    this.discountCodeLabel = this.locale.discountPlaceholder;
    this.passwordLength = AppConfig.passwordLength;

    this.registerForm = this.formBuilder.group({
      firstName: [null, Validators.required],
      secondName: [null, Validators.required],
      email: [null, [Validators.required, Validators.pattern(AppConfig.emailRegex)]],
      emailAgain: [null],
      password: [null, [Validators.required, Validators.minLength(this.passwordLength)]],
      gender: [null, [Validators.required]],
      country: ['ENG', [Validators.required]],
      school: [null],
      emailPreference: [false, Validators.required],
      url: [
        `${window.location.origin}/${ActivationComponent.route}${ActivationComponent.routeParams}`,
        Validators.required
      ],
      terms: [null, Validators.requiredTrue],
      discountCode: [null]
    });

    if (sessionStorage.getItem('registerForm')) {
      this.setValues(JSON.parse(sessionStorage.getItem('registerForm')));
    }

    // check for discount code passed over
    this.route.queryParams.subscribe(params => {
      let discountCode = params['code'];
      if (discountCode) {
        this.validateCode(discountCode);
      } else {
        this.registerForm.controls['email'].enable();
        this.registerForm.controls['emailAgain'].enable();
        this.registerForm.controls['discountCode'].enable();
      }
    });

    this.loadCountries();
    this.loadGenders();

    sessionStorage.setItem('paying', 'true');
  }

  loadCountries() {
    this.userService.getCountries().subscribe(countries => {
      this.countries = _.orderBy(countries, ['priority', 'countryName'], 'asc');
    });
  }

  loadGenders() {
    this.userService.getGenders().subscribe(genders => {
      this.genders = genders;
    });
  }

  validateCode(discountCode: string) {
    var loadingSuccess = false;

    this.registerForm.controls['discountCode'].patchValue(discountCode);
    this.registerForm.controls['discountCode'].disable();
    this.discountCodeLabel = this.locale.discountLoading;
    this.authService
      .checkCode(discountCode)
      .finally(() => {
        if (!loadingSuccess) {
          this.discountCodeLabel = this.locale.discountPlaceholder;
          this.registerForm.controls['email'].enable();
          this.registerForm.controls['emailAgain'].enable();
        }
      })
      .subscribe(
        data => {
          if (data.data) {
            if (data.data.free) {
              this.discountCodeLabel = this.locale.discountFree;
            } else {
              this.discountCodeLabel = this.locale.discountDiscount.format(data.data.description);
            }
            if (data.data.email_address) {
              this.registerForm.controls['email'].patchValue(data.data.email_address);
              this.registerForm.controls['email'].disable();
              this.registerForm.controls['emailAgain'].patchValue(data.data.email_address);
              this.registerForm.controls['emailAgain'].disable();
            }

            loadingSuccess = true;
          }
        },
        err => {}
      );
  }

  setFormSubmitted(value) {
    this.formSubmitted = value;
  }

  ngOnDestroy() {
    let values = [];
    this.getValues().forEach(e => {
      if (e['name'] !== 'password') values.push(e);
    });

    sessionStorage.setItem('registerForm', JSON.stringify(values));
  }

  private getValues(): object[] {
    let values = [];

    Object.keys(this.registerForm.controls).forEach(e => {
      let control = this.registerForm.controls[e];
      values.push({ name: e, value: control.value });
    });

    return values;
  }

  private setValues(values: object[]) {
    values.forEach(e => {
      if (!this.registerForm.controls[e['name']]) {
        sessionStorage.removeItem('registerForm');
        return;
      }
      this.registerForm.controls[e['name']].setValue(e['value']);
    });
  }

  togglePassword() {
    this.showPassword = !this.showPassword;
    this.passwordType = this.showPassword ? 'text' : 'password';
  }

  register(model: RegisterRequestModel) {
    this.registerFormSubmitted = true;

    if (!this.registerForm.valid) {
      this.registerFailed(false);
      return;
    }

    if (
      this.registerForm.controls['email'].value != this.registerForm.controls['emailAgain'].value
    ) {
      this.registerFailed(false);
      return;
    }

    this.setFormSubmitted(true);

    this.authService
      .register(model)
      .finally(() => {
        this.setFormSubmitted(false);
      })
      .subscribe(
        response => {
          if (response.success) {
            document.cookie = 'state=1';
            this.registerSucceeded(response);
          } else {
            this.modal.create(
              this.locale.registerFailedDefaultModalTitle,
              response.returnCode.message
            );
          }
        },
        (err: RegisterResponseModel) => {
          this.registerFailed(true, err);
        }
      );
  }

  registerFailed(isServerError: boolean, err?: RegisterResponseModel) {
    let text = this.locale.registerFailedValidationModalText;

    if (isServerError) {
      text = this.locale.registerFailedDefaultModalText;

      switch (err.returnCode.label) {
        case 'E_EMAIL_TAKEN':
          if (err && err.returnCode) {
            text = err.returnCode.message;
            this.emailError = text;
            text = text + '<br />' + this.locale.registerContactUs;
            this.registerForm.controls['email'].markAsUntouched();
            this.registerForm.controls['email'].markAsPristine();
          }
          break;

        case 'E_DISCOUNT_CODE_REDEEMED':
        case 'E_DISCOUNT_CODE_EXPIRED':
        case 'E_DISCOUNT_CODE_INVALID':
          if (err && err.returnCode) {
            text = err.returnCode.message;
            this.discountCodeError = text;
            text = text + '<br />' + this.locale.registerContactUs;
            this.registerForm.controls['discountCode'].markAsUntouched();
            this.registerForm.controls['discountCode'].markAsPristine();
          }
          break;
      }
    }

    this.toastr.error(text, this.locale.registerFailedDefaultModalTitle, { enableHtml: true });
  }

  registerSucceeded(response?: RegisterResponseModel) {
    if (response.paid == 1) {
      sessionStorage.setItem('freeRedeemed', 'true');
      this.router.navigate([`/${RegisterCompleteComponent.route}`]);
      return;
    }
    this.questionnaireService.requestPaymentGateway();
  }
}



// WEBPACK FOOTER //
// ./src/app/components/account/register/register.component.ts